import type { Finding } from './scan.ts';

/** Fixed-width replacement so the mark never leaks the secret's length. */
export const REDACTION_MARKER = (ruleId: string): string => `[lore-redacted:${ruleId}]`;

export type RedactResult =
  | { ok: true; text: string }
  | { ok: false; reason: 'unredactable' };

// RFC-6901 token decode: ~1 -> /, then ~0 -> ~ (order matters).
function decodeToken(token: string): string {
  return token.replaceAll('~1', '/').replaceAll('~0', '~');
}
function pointerTokens(pointer: string): string[] {
  if (pointer === '') return [];
  return pointer.split('/').slice(1).map(decodeToken);
}
function getAtPointer(root: unknown, pointer: string): unknown {
  let cur: unknown = root;
  for (const token of pointerTokens(pointer)) {
    if (Array.isArray(cur)) cur = cur[Number(token)];
    else if (cur && typeof cur === 'object') cur = (cur as Record<string, unknown>)[token];
    else return undefined;
  }
  return cur;
}
function setAtPointer(root: unknown, pointer: string, value: string): void {
  const tokens = pointerTokens(pointer);
  let cur: unknown = root;
  for (let i = 0; i < tokens.length - 1; i += 1) {
    const token = tokens[i] as string;
    cur = Array.isArray(cur) ? cur[Number(token)] : (cur as Record<string, unknown>)[token];
  }
  const last = tokens[tokens.length - 1] as string;
  if (Array.isArray(cur)) cur[Number(last)] = value;
  else (cur as Record<string, unknown>)[last] = value;
}

/**
 * Apply redact-disposition findings to a JSONL transcript in DECODED space.
 * Only lines that actually carry a redaction are re-serialized; untouched lines
 * pass through byte-for-byte. Returns `{ ok: false }` (→ caller quarantines) if
 * any finding can't be cleanly applied (bad pointer, out-of-range, overlap).
 */
export function redactTranscript(jsonl: string, findings: Finding[]): RedactResult {
  if (findings.length === 0) return { ok: true, text: jsonl };

  const hadTrailingNewline = jsonl.endsWith('\n');
  const lines = jsonl.split('\n');
  if (hadTrailingNewline) lines.pop();

  const byLine = new Map<number, Finding[]>();
  for (const f of findings) {
    byLine.set(f.location.line, [...(byLine.get(f.location.line) ?? []), f]);
  }

  for (const [line, lineFindings] of byLine) {
    const raw = lines[line];
    if (raw === undefined) return { ok: false, reason: 'unredactable' };
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { ok: false, reason: 'unredactable' };
    }

    const byPointer = new Map<string, Finding[]>();
    for (const f of lineFindings) {
      byPointer.set(f.location.jsonPointer, [
        ...(byPointer.get(f.location.jsonPointer) ?? []),
        f,
      ]);
    }

    for (const [pointer, pointerFindings] of byPointer) {
      const leaf = getAtPointer(parsed, pointer);
      if (typeof leaf !== 'string') return { ok: false, reason: 'unredactable' };

      // Several rules routinely flag the SAME secret (e.g. a `KEY=value` caught
      // by both generic-api-key and lore-env-secret-assignment). Coalesce
      // overlapping/touching spans into non-overlapping ranges and redact the
      // union — unambiguous, since redaction only removes text. (Rejecting
      // overlaps here would re-quarantine exactly those multi-rule hits.)
      for (const f of pointerFindings) {
        if (f.start < 0 || f.end > leaf.length || f.end <= f.start) {
          return { ok: false, reason: 'unredactable' };
        }
      }
      const ascending = [...pointerFindings].sort(
        (a, b) => a.start - b.start || a.end - b.end,
      );
      const merged: Array<{ start: number; end: number; ruleIds: Set<string> }> = [];
      for (const f of ascending) {
        const last = merged[merged.length - 1];
        if (last && f.start <= last.end) {
          last.end = Math.max(last.end, f.end);
          last.ruleIds.add(f.ruleId);
        } else {
          merged.push({ start: f.start, end: f.end, ruleIds: new Set([f.ruleId]) });
        }
      }
      // Apply highest-offset-first so earlier offsets stay valid against the
      // original string.
      let result = leaf;
      for (let i = merged.length - 1; i >= 0; i -= 1) {
        const m = merged[i];
        const label = [...m.ruleIds].sort().join('+');
        result = result.slice(0, m.start) + REDACTION_MARKER(label) + result.slice(m.end);
      }
      // An empty pointer addresses the whole line value (a bare top-level
      // JSON string) — there's no parent to set into, so replace the value.
      if (pointer === '') {
        parsed = result;
      } else {
        setAtPointer(parsed, pointer, result);
      }
    }

    lines[line] = JSON.stringify(parsed);
  }

  return { ok: true, text: lines.join('\n') + (hadTrailingNewline ? '\n' : '') };
}
